import { useRouteQuery } from '@vueuse/router'
import { PAGE_ID_QUERY } from '~/constants/urls'

export interface ReadingPosition {
  pageNumber: string | undefined
  anchor: string | undefined
}

/**
 * URL as source of truth for reading position (?pageId=5&targetId=section-1).
 * - updateUrl(): changes refs (triggers watchers)
 * - updateUrlSilently(): history.replaceState (no reactivity, debounced)
 */
export function useBookUrl() {
  const pageId = useRouteQuery<string | undefined>(PAGE_ID_QUERY)
  const targetId = useRouteQuery<string | undefined>('targetId')

  // Handle pipe-separated targetId format: "pageId|anchor" → split into pageId and targetId
  watch(targetId, (value) => {
    if (value?.includes('|')) {
      const [page, anchor] = value.split('|')
      pageId.value = page
      targetId.value = anchor
    }
  }, { immediate: true })

  const urlPosition = computed<ReadingPosition>(() => {
    return { pageNumber: pageId.value?.trim() || undefined, anchor: targetId.value?.trim() || undefined }
  })

  const urlPositionKey = computed(() => {
    const { pageNumber, anchor } = urlPosition.value
    return `${pageNumber ?? ''}#${anchor ?? ''}`
  })

  /** Triggers watchers - use for intentional navigation (TOC clicks, search). */
  function updateUrl(newPosition: ReadingPosition) {
    pageId.value = newPosition.pageNumber || undefined
    targetId.value = newPosition.anchor || undefined
  }

  /** No reactivity trigger - use for scroll position updates. */
  const updateUrlSilently = useDebounceFn((newPosition: ReadingPosition) => {
    if (!import.meta.client) {
      return
    }

    const url = new URL(window.location.href)

    if (newPosition.pageNumber) {
      url.searchParams.set(PAGE_ID_QUERY, newPosition.pageNumber)
    }
    else { url.searchParams.delete(PAGE_ID_QUERY) }
    if (newPosition.anchor) {
      url.searchParams.set('targetId', newPosition.anchor)
    }
    else { url.searchParams.delete('targetId') }

    window.history.replaceState(history.state, '', url.toString())
  }, 300)

  // Reads the current pageId from actual URL, needed because
  // updateUrlSilently doesn't trigger reactivity, so urlPosition won't update until next reactive change.
  function getCurrentPageId(): string | undefined {
    if (!import.meta.client) {
      return undefined
    }
    const url = new URL(window.location.href)
    return url.searchParams.get(PAGE_ID_QUERY) || undefined
  }

  return {
    urlPosition: readonly(urlPosition),
    urlPositionKey,
    updateUrl,
    updateUrlSilently,
    getCurrentPageId,
  }
}
